/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/** One document the OCR index matched and the repository allowed. */
export interface OcrHit {
  nodeId: string;
  name: string;
  path: string;
  page: number;
  score: number;
  /** Text around the match, with the term wrapped in `<em>` by Elasticsearch. */
  fragments: string[];
  /** Corners of the first line carrying the term, in source-image pixels. */
  firstBox: number[] | null;
  modifiedAt: string | null;
  modifiedBy: string | null;
  sizeInBytes: number | null;
  title: string | null;
}

export interface OcrSearchResult {
  term: string;
  durationMs: number;
  /** Documents matched that the reader may open — never a count of the others. */
  found: number;
  hits: OcrHit[];
}

/**
 * Searches the words read out of scanned documents.
 *
 * Alfresco indexes what it can extract from a file, which for a scan is nothing,
 * and its own analyser treats a Thai sentence as a single token — so a reader
 * searching for a word in the middle of a Thai phrase finds nothing at all. The
 * OCR sidecar keeps a second index for exactly those words, analysed for Thai.
 *
 * The search runs on the sidecar rather than here because the answer carries the
 * words around each hit, and those belong to documents this reader may not be
 * allowed to open. The sidecar puts the ids past Alfresco first, as this user,
 * and only answers with what comes back — so the repository stays the one judge
 * of who reads what, and nothing forbidden ever reaches the browser.
 */
@Injectable({ providedIn: 'root' })
export class OcrSearchService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly config = inject(AppConfigService);

  private get baseUrl(): string {
    return this.config.get<string>('ocr.baseUrl', '/ocr-api');
  }

  /** True when the sidecar is reachable. Its index is made on demand. */
  available(): Observable<boolean> {
    return this.http.get(`${this.baseUrl}/api/ocr/health`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  search(term: string, maxItems = 25): Observable<OcrSearchResult | null> {
    const trimmed = (term ?? '').trim();
    if (!trimmed) {
      return of(null);
    }

    return this.http
      .post<OcrSearchResult>(`${this.baseUrl}/api/ocr/search`, { term: trimmed, maxItems }, { headers: this.credentials() })
      .pipe(catchError(() => of(null)));
  }

  /**
   * The sidecar asks Alfresco on this reader's behalf, so it needs credentials
   * Alfresco accepts. A ticket goes in the `ROLE_TICKET` form, which is what the
   * repository expects when the password itself is not being sent.
   */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}
